// @flow
import moment from 'moment';
import Campaign from 'common/modules/campaign/domain/Campaign';
import Radio from 'common/domain/radio';

export type CampaignStatus =
| 'RUNNING'
| 'NOT_LINKED'
| 'NOT_STARTED'
| 'TO_COMPLETE'
| 'COMPLETED';

export type CampaignActions =
| 'LINK'
| 'COMPLETE';

export type CampaignCustomer = {
  reference: string,
  name: string,
}

export type ExternalCampaignListing = {
  name: string,
}

export interface LinkableCampaign {
  isLinked(): boolean;

  getReference(): string;

  getLinkedExternalCampaigns(): ExternalCampaignListing[];
}

function getNumberOfDaysUntilToday(date: moment) {
  return moment().diff(date, 'days');
}

function getNewCampaignStatus(campaign: Campaign, radioHasConnectors): CampaignStatus {
  if (campaign.isRunning()) {
    if (campaign.canBeCompleted()) {
      return 'TO_COMPLETE';
    }

    if (!campaign.isLinked() && radioHasConnectors) {
      return 'NOT_LINKED';
    }

    return 'RUNNING';
  }

  return 'NOT_STARTED';
}

export default class CampaignV2Listing implements LinkableCampaign {
  reference: string;
  status: CampaignStatus;
  startOn: moment;
  name: string;
  callSign: string;
  linkedTo: ExternalCampaignListing[];
  totalCost: number;
  customer: CampaignCustomer;

  constructor({ reference, status, startOn, name, callSign, linkedTo, totalCost, customer }: $Shape<CampaignV2Listing>) {
    this.reference = reference;
    this.status = status;
    this.startOn = startOn;
    this.name = name;
    this.callSign = callSign;
    this.linkedTo = linkedTo;
    this.totalCost = totalCost;
    this.customer = customer;
  }

  hasStartedToday() {
    return getNumberOfDaysUntilToday(this.startOn) === 0;
  }

  hasStartedYesterday() {
    return getNumberOfDaysUntilToday(this.startOn) === 1;
  }

  getActions(radio: Radio): CampaignActions | void {
    if (this.status === 'TO_COMPLETE') {
      return 'COMPLETE';
    }

    if (!radio.hasConnector()) {
      return undefined;
    }

    if (this.status === 'NOT_LINKED') {
      return 'LINK';
    }

    if (this.status === 'NOT_STARTED' && !this.isLinked()) {
      return 'LINK';
    }

    return undefined;
  }

  isLinked(): boolean {
    return this.linkedTo.length > 0;
  }

  getReference(): string {
    return this.reference;
  }

  getLinkedExternalCampaigns(): ExternalCampaignListing[] {
    return this.linkedTo;
  }

  static fromNew(campaign: Campaign, radioHasConnectors: boolean) {
    return new CampaignV2Listing({
      reference: campaign.reference,
      status: getNewCampaignStatus(campaign, radioHasConnectors),
      startOn: campaign.getStartAt(),
      name: campaign.getName(),
      linkedTo: [],
      totalCost: campaign.getTotalCost(),
      customer: {
        reference: campaign.advertiser.reference,
        name: campaign.advertiser.name,
      },
    });
  }

  static parse(data: Object) {
    return new CampaignV2Listing({
      reference: data.reference,
      status: data.status,
      startOn: moment(data.startOn),
      callSign: data.callSign,
      name: data.name,
      linkedTo: data.linkedTo,
      totalCost: data.totalCost,
      customer: data.customer,
    });
  }
}



// WEBPACK FOOTER //
// ./src/statsradio/modules/campaigns/domain/CampaignV2Listing.js